import {
  ActionButton,
  Dialog,
  DialogFooter,
  Layer,
  mergeStyles,
  Spinner,
  SpinnerSize,
  Stack,
  ThemeProvider,
} from '@fluentui/react';
import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { v4 } from 'uuid';

import { store, useAppSelector } from '../../../App/store';
import { getAsync } from '../../../utils/api';
import { theme } from '../../../utils/theme';
import viewPDF from '../List/functions/viewPDF';
import Create from '../New/Components/FormButtons/Create';
import DeleteForm from '../New/Components/FormButtons/DeleteForm';
import PinSection from '../New/Components/PinSection';
import { actions, useFormSelector } from '../slice';

type Props = {
  formId: string;
  title?: string;
  hidden?: boolean;
  children?: JSX.Element | JSX.Element[];
  onClosed?: (...args: any) => void;
};

export const openMiniEditor = (props: Props) => {
  const div = document.createElement("div");
  document.body.appendChild(div);

  let onClosed = props.onClosed;
  props.onClosed = () => {
    ReactDOM.unmountComponentAtNode(div);
    div.remove();
    onClosed?.();
    console.log("removed mini editor modal");
  };

  ReactDOM.render(
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <MiniEditor {...props} />
      </ThemeProvider>
    </Provider>,
    div
  );
};

export const MiniEditor = (props: Props) => {
  const employee = useFormSelector((state) => state.employee);
  const editingNewFormUUID = useAppSelector(
    (state) => state.currentForm.editingNewFormUUID
  );
  const uuid = useMemo(() => v4(), []);
  const [isLoading, setLoading] = useState(true);
  const [customerDisplayName, setCustomerDisplayName] = useState("-");

  const onCompleted = (success?: boolean) => {
    if (success) {
      props.onClosed?.();
    }
  };

  useEffect(() => {
    let destroyed = false;

    getAsync("/forms/get?id=" + props.formId)
      .then((res) => {
        if (destroyed) return;
        if (!res) throw "No response";

        actions.createNewForm(uuid);
        actions.setVariable(["editingNewFormUUID", uuid]);
        actions.setFormVariable(["editingFormId", props.formId]);
        actions.setFormData(res.data);

        setCustomerDisplayName(res.data.account.displayName);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        alert("Hata oluştu: Form yüklenemedi");
      });

    return () => {
      destroyed = true;
      actions.deleteNewForm(uuid);
    };
  }, [uuid]);

  if (!isLoading && editingNewFormUUID !== uuid) {
    return <></>;
  }

  return (
    <Layer>
      <Dialog
        styles={{ main: { maxWidth: "unset !important" } }}
        dialogContentProps={{
          title: (
            <Stack horizontal>
              <Stack>
                {props.title || "Mini Editor"}
                &nbsp; &nbsp; &nbsp; &nbsp;
                {!isLoading && (
                  <ActionButton
                    iconProps={{ iconName: "PDF" }}
                    text="Önceki PDF görüntüle"
                    onClick={() => {
                      setLoading(true);
                      viewPDF(props.formId)
                        .then(() => setLoading(false))
                        .catch(() => alert("Hata oluştu"));
                    }}
                  />
                )}
              </Stack>
              <Stack grow horizontalAlign="end" className={detailsFontStyles}>
                <Stack.Item>
                  <b>ID:</b> {props.formId}
                </Stack.Item>
                <Stack.Item>
                  <b>UUID:</b> {uuid}
                </Stack.Item>
                <Stack.Item>
                  <b>Müşteri:</b> {customerDisplayName}
                </Stack.Item>
                <Stack.Item>
                  <b>Temsilci:</b> {employee?.name || "-"}
                </Stack.Item>
              </Stack>
            </Stack>
          ),
        }}
        hidden={props.hidden === undefined ? false : props.hidden}
      >
        {isLoading ? (
          <Spinner
            size={SpinnerSize.large}
            style={{ padding: "1rem" }}
            label="Yükleniyor"
          />
        ) : (
          <>
            {!employee && <PinSection />}

            <div style={{ display: employee ? "unset" : "none" }}>
              {props.children}
              <DialogFooter>
                <DeleteForm skipConfirm onCompleted={onCompleted} />
                <Create fastMode onCompleted={onCompleted} />
              </DialogFooter>
            </div>
          </>
        )}
      </Dialog>
    </Layer>
  );
};

const detailsFontStyles = mergeStyles({
  "& *": {
    fontSize: "0.75rem !important",
    color: theme.palette.neutralTertiary,
  },
});
